import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@ambernord/ui';
import { CheckCircle2, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';
import type { SupplyOrderDto } from './types.js';

/**
 * Inspekcijas solis (Phase 5b): "viss kārtībā" apstiprinājums (delivered → inspected)
 * VAI reklamācijas celšana (komentārs + veids) → mediētais pavediens.
 * Redzams tikai platformas/klienta lomām pie delivered/inspected — piegādātājs ir
 * inspicētā puse un reklamāciju celt nedrīkst.
 */
export function ClaimPanel({
  so,
  onChanged,
}: {
  so: SupplyOrderDto;
  onChanged: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('sodetail');
  const { toast } = useToast();

  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [claimType, setClaimType] = useState<'visible' | 'latent'>('visible');
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  async function confirmOk() {
    setBusy(true);
    try {
      const res = await api.post<{ ok?: boolean; error?: string }>(
        `/api/claims/inspection/${so.id}/confirm`,
      );
      if (res.ok) {
        toast(t('claim.okSuccess'));
        await onChanged();
      } else {
        toast(res.data.error ?? t('claim.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitClaim() {
    setFormError(null);
    if (!comment.trim()) {
      setFormError(t('claim.missingComment'));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ claimId?: string; threadId?: string; error?: string }>(
        `/api/claims/inspection/${so.id}/claim`,
        { type: claimType, comment: comment.trim() },
      );
      if (res.ok && res.data.threadId) {
        setThreadId(res.data.threadId);
        setOpen(false);
        setComment('');
        toast(t('claim.raised'));
        await onChanged();
      } else {
        toast(res.data.error ?? t('claim.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('claim.title')} subtitle={t('claim.hint')} />
      <CardBody>
        {so.status === 'inspected' && so.inspectedAt ? (
          <div className="mb-3">
            <Badge tone="green">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              {t('claim.inspectedAt', { at: formatDateTime(so.inspectedAt, i18n.language) })}
            </Badge>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {so.status === 'delivered' ? (
            <Button disabled={busy} onClick={() => void confirmOk()}>
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {t('claim.ok')}
            </Button>
          ) : null}
          <Button variant="secondary" disabled={busy} onClick={() => setOpen(true)}>
            {t('claim.raise')}
          </Button>
          {threadId ? (
            <Link
              to="/chat"
              state={{ threadId }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              <MessageSquare className="h-4 w-4" aria-hidden />
              {t('claim.openChat')}
            </Link>
          ) : null}
        </div>
      </CardBody>

      <Modal open={open} onClose={() => setOpen(false)} title={t('claim.modalTitle')}>
        <div className="space-y-3">
          <Field label={t('claim.type')} htmlFor="claim-type">
            <Select
              id="claim-type"
              value={claimType}
              onChange={(e) => setClaimType(e.target.value === 'latent' ? 'latent' : 'visible')}
            >
              <option value="visible">{t('claim.types.visible')}</option>
              <option value="latent">{t('claim.types.latent')}</option>
            </Select>
          </Field>
          <Field label={t('claim.comment')} htmlFor="claim-comment" error={formError}>
            <Textarea
              id="claim-comment"
              value={comment}
              placeholder={t('claim.commentPlaceholder')}
              onChange={(e) => setComment(e.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('claim.cancel')}
            </Button>
            <Button disabled={busy} onClick={() => void submitClaim()}>
              {t('claim.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
